/**
 * Table.tsx — the ONE admin data-table assembly.
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: P0 (portal foundation).
 *
 * WHY: every admin tab hand-assembled the same TableScroll + `<table>` +
 * `<thead>` + per-cell style spreads. `DataTable` centralises that shell
 * (accessible name, full width, collapsed borders) and `Th`/`Td` are thin
 * wrappers over the existing shared cell styles — a `style` prop keeps the
 * few per-column tweaks (right-aligned £ columns, nowrap) local to the
 * caller. Purely structural: no data logic lives here.
 */
import type { CSSProperties, ReactNode } from 'react';
import { TableScroll, tableCellStyle, tableHeadCellStyle } from './kit';

/** A header cell on the shared admin-table style (always `scope="col"`). */
export function Th({ children, style }: { children?: ReactNode; style?: CSSProperties }) {
  return (
    <th scope="col" style={{ ...tableHeadCellStyle, ...style }}>
      {children}
    </th>
  );
}

/** A body cell on the shared admin-table style. `colSpan` lets a full-width
 * detail row (the audit tracer's expandable context, UX Wave 3c) span the table. */
export function Td({ children, style, colSpan }: { children?: ReactNode; style?: CSSProperties; colSpan?: number }) {
  return (
    <td colSpan={colSpan} style={{ ...tableCellStyle, ...style }}>
      {children}
    </td>
  );
}

/**
 * The admin data table: TableScroll wrapping an aria-labelled table. `head`
 * is the header row's cells (`<Th>…`), `children` the `<tbody>` rows.
 */
export function DataTable({ label, head, children }: { label: string; head: ReactNode; children: ReactNode }) {
  return (
    <TableScroll>
      <table aria-label={label} style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr>{head}</tr>
        </thead>
        <tbody>{children}</tbody>
      </table>
    </TableScroll>
  );
}
